import { useState, useRef, useCallback } from "react";
import type { Contact, ContactGroup } from "../types";
import { GROUP_COLORS } from "../types";

interface ParsedRow {
  name: string;
  phone: string;
  email?: string;
  memo?: string;
  valid: boolean;
  error?: string;
}

interface Props {
  open: boolean;
  groups: ContactGroup[];
  onClose: () => void;
  onImport: (contacts: Omit<Contact, "id" | "createdAt">[], groupIds: string[]) => void;
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}

function parseCSV(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return [];

  // Detect header
  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes("이름") || firstLine.includes("name") || firstLine.includes("전화") || firstLine.includes("phone");
  const dataLines = hasHeader ? lines.slice(1) : lines;

  return dataLines.map((line) => {
    const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    const name = cols[0] ?? "";
    const rawPhone = cols[1] ?? "";
    const email = cols[2] ?? "";
    const memo = cols[3] ?? "";

    const phone = formatPhone(rawPhone);
    const digits = rawPhone.replace(/\D/g, "");

    if (!name) return { name, phone, email: email || undefined, memo: memo || undefined, valid: false, error: "이름 없음" };
    if (digits.length < 10) return { name, phone: rawPhone, email: email || undefined, memo: memo || undefined, valid: false, error: "전화번호 오류" };

    return { name, phone, email: email || undefined, memo: memo || undefined, valid: true };
  });
}

const SAMPLE_CSV = `이름,전화번호,이메일,메모
홍길동,010-1234-5678,hong@example.com,VIP 고객
김철수,010-2345-6789,kim@example.com,
이영희,010-3456-7890,,신규 가입`;

export default function CsvImportModal({ open, groups, onClose, onImport }: Props) {
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      setParsedRows(rows);
      setStep("preview");
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, []);

  const handlePasteText = (text: string) => {
    const rows = parseCSV(text);
    setParsedRows(rows);
    setFileName("직접 입력");
    setStep("preview");
  };

  const validRows = parsedRows.filter((r) => r.valid);
  const invalidRows = parsedRows.filter((r) => !r.valid);

  const handleImport = () => {
    const contacts = validRows.map((r) => ({
      name: r.name,
      phone: r.phone,
      email: r.email,
      memo: r.memo,
      groupIds: selectedGroupIds,
    }));
    onImport(contacts, selectedGroupIds);
    setStep("upload");
    setParsedRows([]);
    setSelectedGroupIds([]);
    setFileName("");
  };

  const toggleGroup = (gid: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(gid) ? prev.filter((id) => id !== gid) : [...prev, gid]
    );
  };

  const downloadSample = () => {
    const blob = new Blob(["\uFEFF" + SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div className="relative w-full md:max-w-xl bg-[#0d0d1a] border border-white/[0.1] rounded-t-2xl md:rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 md:py-5 border-b border-white/[0.08]">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30">
              <i className="ri-file-upload-line text-emerald-400 text-sm md:text-base"></i>
            </div>
            <div>
              <h2 className="text-white font-semibold text-sm md:text-base">CSV 가져오기</h2>
              {step === "preview" && (
                <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">{fileName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors rounded-lg hover:bg-white/[0.06]"
          >
            <i className="ri-close-line text-base md:text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-3 md:p-6 overflow-y-auto flex-1">
          {step === "upload" ? (
            <div className="space-y-3 md:space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-2 md:gap-3 h-32 md:h-40 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                  isDragging
                    ? "border-violet-500/60 bg-violet-500/10"
                    : "border-white/[0.12] hover:border-white/[0.25] hover:bg-white/[0.02]"
                }`}
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/[0.05]">
                  <i className="ri-upload-cloud-2-line text-gray-400 text-xl md:text-2xl"></i>
                </div>
                <div className="text-center px-4">
                  <p className="text-white text-xs md:text-sm font-medium">CSV 파일을 드래그하거나 클릭하여 업로드</p>
                  <p className="text-gray-500 text-[10px] md:text-xs mt-1">UTF-8 인코딩 CSV 파일 지원</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                  className="hidden"
                />
              </div>

              {/* Format guide */}
              <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3 md:p-4">
                <div className="flex items-center justify-between mb-2 md:mb-3">
                  <p className="text-gray-300 text-[10px] md:text-xs font-semibold">CSV 형식 안내</p>
                  <button
                    onClick={downloadSample}
                    className="flex items-center gap-1 text-[10px] md:text-xs text-violet-400 hover:text-violet-300 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-download-line text-xs md:text-sm"></i>
                    샘플 다운로드
                  </button>
                </div>
                <div className="bg-[#080812] rounded-lg p-2 md:p-3 font-mono text-[10px] md:text-xs text-gray-400 leading-relaxed">
                  <p className="text-emerald-400">이름,전화번호,이메일,메모</p>
                  <p>홍길동,010-1234-5678,hong@example.com,VIP 고객</p>
                  <p>김철수,010-2345-6789,,</p>
                </div>
                <p className="text-gray-600 text-[10px] md:text-xs mt-2">* 이름, 전화번호는 필수 / 이메일, 메모는 선택</p>
              </div>

              {/* Direct paste */}
              <div>
                <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">또는 직접 붙여넣기</p>
                <textarea
                  placeholder={"이름,전화번호,이메일,메모\n홍길동,010-1234-5678,,"}
                  rows={4}
                  onBlur={(e) => { if (e.target.value.trim()) handlePasteText(e.target.value); }}
                  className="w-full bg-white/[0.04] border border-white/[0.1] rounded-xl px-3 md:px-4 py-2.5 md:py-3 text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all resize-none font-mono"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 md:gap-3">
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-2 md:p-3 text-center">
                  <p className="text-white text-lg md:text-xl font-bold">{parsedRows.length}</p>
                  <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">전체</p>
                </div>
                <div className="bg-emerald-500/[0.06] border border-emerald-500/20 rounded-xl p-2 md:p-3 text-center">
                  <p className="text-emerald-400 text-lg md:text-xl font-bold">{validRows.length}</p>
                  <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">가져오기 가능</p>
                </div>
                <div className={`rounded-xl p-2 md:p-3 text-center border ${invalidRows.length > 0 ? "bg-red-500/[0.06] border-red-500/20" : "bg-white/[0.03] border-white/[0.07]"}`}>
                  <p className={`text-lg md:text-xl font-bold ${invalidRows.length > 0 ? "text-red-400" : "text-gray-600"}`}>{invalidRows.length}</p>
                  <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">오류</p>
                </div>
              </div>

              {/* Group assignment */}
              <div>
                <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">그룹 지정 (선택)</p>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {groups.map((g) => {
                    const cfg = GROUP_COLORS.find((c) => c.value === g.color) ?? GROUP_COLORS[0];
                    const isSelected = selectedGroupIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        onClick={() => toggleGroup(g.id)}
                        className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
                          isSelected ? `${cfg.bg} ${cfg.text} ${cfg.border}` : "bg-white/[0.04] text-gray-500 border-white/[0.08] hover:text-gray-300"
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? cfg.dot : "bg-gray-600"}`}></span>
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Preview table */}
              <div className="bg-white/[0.02] border border-white/[0.07] rounded-xl overflow-hidden max-h-40 md:max-h-52 overflow-y-auto">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[320px]">
                    <thead className="sticky top-0 bg-[#0d0d1a]">
                      <tr className="border-b border-white/[0.07]">
                        <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-2 md:px-4 py-2 md:py-2.5 whitespace-nowrap">상태</th>
                        <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-2 md:px-3 py-2 md:py-2.5 whitespace-nowrap">이름</th>
                        <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-2 md:px-3 py-2 md:py-2.5 whitespace-nowrap">전화번호</th>
                        <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-2 md:px-3 py-2 md:py-2.5 whitespace-nowrap">이메일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, i) => (
                        <tr key={i} className={`border-b border-white/[0.04] last:border-0 ${!row.valid ? "bg-red-500/[0.04]" : ""}`}>
                          <td className="px-2 md:px-4 py-2 md:py-2.5">
                            {row.valid ? (
                              <span className="flex items-center gap-1 text-emerald-400 text-[10px] md:text-xs whitespace-nowrap">
                                <i className="ri-checkbox-circle-line text-xs md:text-sm"></i>
                                정상
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-400 text-[10px] md:text-xs whitespace-nowrap">
                                <i className="ri-close-circle-line text-xs md:text-sm"></i>
                                {row.error}
                              </span>
                            )}
                          </td>
                          <td className="px-2 md:px-3 py-2 md:py-2.5 text-white text-[10px] md:text-xs whitespace-nowrap">{row.name || "-"}</td>
                          <td className="px-2 md:px-3 py-2 md:py-2.5 text-gray-300 text-[10px] md:text-xs font-mono whitespace-nowrap">{row.phone || "-"}</td>
                          <td className="px-2 md:px-3 py-2 md:py-2.5 text-gray-400 text-[10px] md:text-xs max-w-[80px] md:max-w-[120px] truncate">{row.email || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Back button */}
              <button
                onClick={() => { setStep("upload"); setParsedRows([]); setFileName(""); }}
                className="flex items-center gap-1 text-[10px] md:text-xs text-gray-500 hover:text-gray-300 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-left-line text-xs md:text-sm"></i>
                다시 업로드
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "preview" && (
          <div className="px-4 md:px-6 py-3 md:py-4 border-t border-white/[0.08] flex gap-2 md:gap-3">
            <button
              onClick={handleImport}
              disabled={validRows.length === 0}
              className="flex-1 py-2 md:py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-40 text-white text-xs md:text-sm font-bold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-1.5 md:gap-2"
            >
              <i className="ri-user-add-line text-sm md:text-base"></i>
              {validRows.length}명 가져오기
            </button>
            <button
              onClick={onClose}
              className="px-4 md:px-5 py-2 md:py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
            >
              취소
            </button>
          </div>
        )}
      </div>
    </div>
  );
}